<template>
  <div class="chat-app">
    <Navbar 
      @show-chat-info="showChatInfo = true"
      @show-who-we-are="showWhoWeAre = true"
    />
    
    <ChatInfoModal 
      :show="showChatInfo"
      @close="closeModals"
    />

    <WhoWeAreModal 
      :show="showWhoWeAre"
      @close="closeModals"
    />
    
    <div class="main-content">
      <HeroCard />
      
      <SearchBar
        v-model:searchQuery="searchQuery"
        v-model:searchMode="searchMode"
        v-model:startDate="startDate"
        v-model:endDate="endDate"
        :resultCount="filteredMessages.length"
        @dateRangeChange="handleDateRangeChange"
        @clearFilters="clearFilters"
      />
      
      <MessageList
        :messages="displayedMessages"
        :searchQuery="effectiveSearchQuery"
        :highlightedMessageId="highlightedMessageId"
        :isLoading="isLoading"
        @scroll="handleScroll"
      />
    </div>
  </div>
</template>

<script>
import messages from './messages.json';
import Navbar from './components/Navbar.vue';
import ChatInfoModal from './components/ChatInfoModal.vue';
import WhoWeAreModal from './components/WhoWeAreModal.vue';
import SearchBar from './components/SearchBar.vue';
import MessageList from './components/MessageList.vue';
import HeroCard from './components/HeroCard.vue';

export default {
  name: 'App',
  components: {
    Navbar,
    ChatInfoModal,
    WhoWeAreModal,
    SearchBar,
    MessageList,
    HeroCard
  },
  data() {
    return {
      baseUrl: process.env.BASE_URL,
      messages: messages,
      searchQuery: '',
      searchMode: 'contains',
      startDate: '',
      endDate: '',
      highlightedMessageId: null,
      searchTimeout: null,
      displayedMessages: [],
      currentPage: 0,
      messagesPerPage: 50,
      isLoading: false,
      hasMoreMessages: true,
      showChatInfo: false,
      showWhoWeAre: false,
      effectiveSearchQuery: ''
    };
  },
  computed: {
    filteredMessages() {
      let filtered = this.messages;
      
      // Apply date range filter
      if (this.startDate || this.endDate) {
        filtered = filtered.filter(message => {
          const [datePart] = message.timestamp.split(' ');
          const [year, month, day] = datePart.split('-');
          const messageDateStr = `${month}/${day}/${year}`;
          
          if (this.startDate && messageDateStr < this.startDate) return false;
          if (this.endDate && messageDateStr > this.endDate) return false;
          return true;
        });
      }
      
      // Apply text search filter
      if (this.effectiveSearchQuery) {
        const query = this.effectiveSearchQuery.toLowerCase();
        let regex;
        
        try {
          switch (this.searchMode) {
            case 'wholeWord':
              regex = new RegExp(`\\b${this.escapeRegExp(query)}\\b`, 'i');
              break;
            case 'regex':
              regex = new RegExp(query, 'i');
              break;
            default: // 'contains'
              regex = new RegExp(this.escapeRegExp(query), 'i');
          }
        } catch (e) {
          regex = new RegExp(this.escapeRegExp(query), 'i');
        }
        
        filtered = filtered.filter(message => 
          regex.test(message.message) ||
          regex.test(message.sender_alias) ||
          (message.chat_id && regex.test(message.chat_id))
        );
      }
      
      // Sort messages by date in ascending order
      return filtered.sort((a, b) => {
        const dateA = new Date(a.timestamp);
        const dateB = new Date(b.timestamp);
        return dateA - dateB;
      });
    }
  },
  watch: {
    searchQuery(newValue) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.effectiveSearchQuery = newValue;
        this.resetPagination();
        this.loadMoreMessages();
      }, 300);
    },
    searchMode() {
      this.resetPagination();
      this.loadMoreMessages();
    },
    startDate() {
      this.resetPagination();
      this.loadMoreMessages();
    },
    endDate() {
      this.resetPagination();
      this.loadMoreMessages();
    }
  },
  methods: {
    handleScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollHeight - scrollTop - clientHeight < 100 && !this.isLoading && this.hasMoreMessages) {
        this.loadMoreMessages();
      }
    },
    loadMoreMessages() {
      this.isLoading = true;
      const start = this.currentPage * this.messagesPerPage;
      const end = start + this.messagesPerPage;
      const newMessages = this.filteredMessages.slice(start, end);
      
      if (newMessages.length === 0) {
        this.hasMoreMessages = false;
      } else {
        this.displayedMessages = [...this.displayedMessages, ...newMessages];
        this.currentPage++;
      }
      
      this.isLoading = false;

      // Remove loading screen after first batch of messages is loaded
      if (this.currentPage === 1) {
        const loadingScreen = document.getElementById('app-loading');
        if (loadingScreen) {
          loadingScreen.remove();
        }
      }
    },
    handleDateRangeChange() {
      this.resetPagination();
      this.loadMoreMessages();
    },
    clearFilters() {
      this.searchQuery = '';
      this.startDate = '';
      this.endDate = '';
      this.effectiveSearchQuery = '';
      this.resetPagination();
      this.loadMoreMessages();
    },
    resetPagination() {
      this.displayedMessages = [];
      this.currentPage = 0;
      this.hasMoreMessages = true;
    },
    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    closeModals() {
      this.showChatInfo = false;
      this.showWhoWeAre = false;
    },
    handleUrlParameters() {
      const params = new URLSearchParams(window.location.search);
      const query = params.get('q');
      const mode = params.get('mode');
      const start = params.get('start');
      const end = params.get('end');

      if (query) this.searchQuery = query;
      if (mode) this.searchMode = mode;
      if (start) this.startDate = start;
      if (end) this.endDate = end;

      if (query || mode || start || end) {
        this.effectiveSearchQuery = query || '';
        this.resetPagination();
        this.loadMoreMessages();
      }
    }
  },
  mounted() {
    document.title = 'Basta Chats from Armada';
    this.loadMoreMessages();
    this.handleUrlParameters();
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap');

:root {
  --primary: #0194FF;
  --background: #1a1a1a;
  --card-background: #242424;
  --text-primary: #ffffff;
  --text-secondary: #a0a0a0;
  --border: #333333;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  background-color: var(--background);
  background-image: url('@/assets/armada-Background-1.png');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: var(--text-primary);
  line-height: 1.5;
}

.chat-app {
  min-height: 100vh;
  display: flex;
}

.main-content {
  flex: 1;
  margin-left: 300px;
  padding: 2rem;
  max-width: calc(100% - 300px);
  transition: margin-left 0.3s ease;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    max-width: 100%;
    padding-top: 4rem;
  }
}

.loading-indicator {
  text-align: center;
  color: var(--text-secondary);
  padding: 1rem;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: var(--background);
  padding: 2rem;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
}

.close-button:hover {
  color: var(--text-primary);
}
</style> 